import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next';

import PageImg from "../../assets/images/404.png";
import { isLoggedIn } from '../../../auth/session';

/**
 * Where a mistyped or dead URL lands.
 *
 * ── WHAT THIS USED TO BE ───────────────────────────────────────────────────
 *
 * One `<img>` and nothing else. No words, no heading, and — the part that
 * mattered — no way out. Somebody who followed a stale bookmark, or a link a
 * colleague pasted from a different install, arrived at a picture and a dead
 * end; the only route back was the browser's back button or retyping the
 * address. Staff on a counter PC do neither, they call somebody.
 *
 * It was also the whole screen's only content, so an asset that fails to load
 * — a cache miss, a bad deploy, a proxy stripping images — left a genuinely
 * blank page, and a blank page reads as "the system is down" rather than "that
 * address does not exist".
 *
 * And to a screen reader it was an alt attribute. That is the entire page.
 *
 * ── WHAT IT IS NOW ─────────────────────────────────────────────────────────
 *
 * The picture stays, marked decorative so it is not announced twice, and the
 * page says in words what happened and offers the way back. Where "back" goes
 * depends on whether anybody is signed in: sending a logged-out visitor to the
 * dashboard would bounce them to the login screen through a redirect, which
 * looks like the app losing its place.
 */
export default function Notfound() {
    const { t } = useTranslation();

    const home = isLoggedIn() ? '/dashboard' : '/login';

    return (
        <div className="center-screen">
            {/* Decorative: the heading below carries the meaning, and an alt
                text repeating it would be announced twice. */}
            <img className="animated fadeIn" src={PageImg} alt="" aria-hidden="true" />

            <h1 className="mt-3">
                {t('empty.notFoundTitle', { defaultValue: 'That page does not exist' })}
            </h1>
            <p className="text-muted">
                {t('empty.notFoundBody', {
                    defaultValue:
                        'The address may have been mistyped, or the screen may have moved. Nothing you were working on is affected.',
                })}
            </p>

            <Link className="btn btn-primary mt-2" to={home}>
                {isLoggedIn()
                    ? t('empty.notFoundHome', { defaultValue: 'Back to the dashboard' })
                    : t('auth.login', { defaultValue: 'Login' })}
            </Link>
        </div>
    )
}
